
import React from "react";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import { auth, db, messaging } from "../../firebase";
import { collection, addDoc, query, where, getDocs, serverTimestamp } from "firebase/firestore";
import { getToken } from "firebase/messaging";
import { persistOrderPdfs, saveOrderPdfsToFiles, extractAllPartyNames } from "./highCourtOrderFiles";
import { getFullCourtName } from "../../utils/courtMapping";
import InfoModal from "../../components/InfoModal";
import { useAuth } from "../../context/AuthContext";
import featureGatingService from "../../services/featureGatingService";
import CaseLimitDialog from "../../components/CaseLimitDialog";
import { apiRequest, HC_API_HOST } from "../../services/fetchCaseApi";
import { statusPillSolid } from "../../styles/statusPill";

function CaseDetailsScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const { caseDetails, status, courtCode, cookies, stateCode, courtComplexCode, cino } = location.state || {};
  const [exporting, setExporting] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [error, setError] = useState("");
  const [userMobile, setUserMobile] = useState("");
  const [caseNumbers, setCaseNumbers] = useState([]);
  const showInfo = () => setIsInfoModalOpen(true);
  const closeInfoModal = () => setIsInfoModalOpen(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateCaseInfo, setDuplicateCaseInfo] = useState(null);
  const { globalUser } = useAuth();
  const userId = globalUser?.uid;
  
  // Case limit states
  const [showCaseLimitDialog, setShowCaseLimitDialog] = useState(false);
  const [caseLimitData, setCaseLimitData] = useState({
    currentCount: 0,
    maxLimit: 0,
    planName: ''
  });

  // Get payload data from localStorage
  const [combinedPayload, setCombinedPayload] = useState(null);

  useEffect(() => {
    // Retrieve and combine payloads from localStorage
    const searchPayload = localStorage.getItem('hc_search_payload');
    const detailsPayload = localStorage.getItem('hc_case_details_payload');

    if (searchPayload || detailsPayload) {
      const combined = {
        searchPayload: searchPayload ? JSON.parse(searchPayload) : null,
        detailsPayload: detailsPayload ? JSON.parse(detailsPayload) : null,
        retrievedAt: new Date().toISOString(),
      };
      setCombinedPayload(combined);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      setError("");
      try {
        const q = query(collection(db, "pending"), where("owner", "==", userId));
        const querySnapshot = await getDocs(q);
        const cases = querySnapshot.docs.map((doc) => doc.data()?.caseNo).filter(Boolean);
        setCaseNumbers(cases);

        const userQuery = query(collection(db, "lawyers"), where("uid", "==", userId));
        const userDocSnapshot = await getDocs(userQuery);
        if (!userDocSnapshot.empty) {
          const userData = userDocSnapshot.docs[0].data();
          setUserMobile(userData?.mobileWithCountryCode || "");
        } else {
          console.warn("User document not found for UID:", userId);
          setUserMobile("");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load required data");
      }
    };
    fetchData();
  }, [userId]);

  const convertDateFormat = (originalDate) => {
    if (!originalDate || typeof originalDate !== "string") return "";
    const dateFormats = [
      /(\d{1,2})(st|nd|rd|th)\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})/i,
      /(\d{1,2})\/(\d{1,2})\/(\d{4})/,
    ];
    for (const format of dateFormats) {
      const match = originalDate.match(format);
      if (match) {
        let day, month, year;
        if (match.length === 5) {
          day = match[1].padStart(2, "0");
          const monthNames = {
            January: "01", February: "02", March: "03", April: "04", May: "05",
            June: "06", July: "07", August: "08", September: "09", October: "10",
            November: "11", December: "12",
          };
          month = monthNames[match[3].charAt(0).toUpperCase() + match[3].slice(1).toLowerCase()];
          year = match[4];
        } else {
          day = match[1].padStart(2, "0");
          month = match[2].padStart(2, "0");
          year = match[3];
        }
        if (day && month && year) return `${day}/${month}/${year}`;
      }
    }
    return originalDate;
  };

  const convertHyphenToSlash = (originalDate) => {
    if (!originalDate || typeof originalDate !== "string") return "";
    const datePattern = /^\d{2}-\d{2}-\d{4}$/;
    return datePattern.test(originalDate) ? originalDate.replace(/-/g, "/") : originalDate;
  };

  const extractPartyName = (advocateString) => {
    if (!advocateString || typeof advocateString !== "string") return "N/A";
    const parts = advocateString.split("Advocate-");
    return parts.length > 0 ? parts[0].trim().replace(/^\d+\) /, "") : "N/A";
  };

  // Extract advocate name(s) from the advocate string
  const extractAdvocateNames = (advocateString) => {
    if (!advocateString || typeof advocateString !== "string") return [];

    // Split by "Advocate-" or "Advocate -" (with space)
    const parts = advocateString.split(/Advocate\s*-\s*/);

    if (parts.length > 1) {
      // Extract advocate names after "Advocate-"
      const advocatesPart = parts[1].trim();

      // Split by comma to handle multiple advocates
      const advocates = advocatesPart
        .split(',')
        .map(adv => {
          // Remove leading numbers like "1)", "2)", etc.
          return adv.trim().replace(/^\d+\)\s*/, '');
        })
        .filter(adv =>
          adv &&
          adv !== '' &&
          adv !== '1123' &&
          !adv.toUpperCase().includes('AND') && // Filter out "AND X OTHERS"
          adv.length > 1 // Filter out single characters
        );

      return advocates;
    }

    return [];
  };

  // Extract all unique advocates from petitioner advocate array
  const extractPetitionerAdvocates = (petitionerAdvocateArray) => {
    const advocates = new Set();
    if (Array.isArray(petitionerAdvocateArray)) {
      petitionerAdvocateArray.forEach(advString => {
        const advocateNames = extractAdvocateNames(advString);
        advocateNames.forEach(adv => advocates.add(adv));
      });
    }
    return Array.from(advocates);
  };

  // Extract all unique advocates from respondent advocate array
  const extractRespondentAdvocates = (respondentAdvocateArray) => {
    const advocates = new Set();
    if (Array.isArray(respondentAdvocateArray)) {
      respondentAdvocateArray.forEach(advString => {
        const advocateNames = extractAdvocateNames(advString);
        advocateNames.forEach(adv => advocates.add(adv));
      });
    }
    return Array.from(advocates);
  };

  // Check case limit before exporting using centralized service
  const checkCaseLimitBeforeExport = async () => {
    if (!globalUser?.uid) return false;

    const caseStatus = status?.toLowerCase() || "pending";
    const targetCollection = caseStatus === "disposed" ? "disposed" : "pending";

    const result = await featureGatingService.checkCaseLimitBeforeExport(
      globalUser.uid,
      1, // Exporting 1 case
      targetCollection
    );

    if (!result.canProceed) {
      setCaseLimitData({
        currentCount: result.currentCount,
        maxLimit: result.maxLimit,
        planName: result.planName
      });
      setShowCaseLimitDialog(true);
      return false;
    }

    return true;
  };

  const handleExportCase = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        alert("User not authenticated");
        return;
      }
      if (!caseDetails) {
        alert("No case details available to export");
        return;
      }

      // Check case limit before proceeding
      const canProceed = await checkCaseLimitBeforeExport();
      if (!canProceed) {
        return; // Stop if limit exceeded
      }

      setExporting(true);

      // Check for duplicate case first
      const caseNo = caseDetails?.caseDetails?.["Registration Number"] || "";

      if (caseNo) {
        // Determine which collection to check based on status
        const statusToCheck = status?.toLowerCase() || "unknown";

        // Check in the appropriate collection based on status
        const collectionName = statusToCheck === "pending" ? "pending" :
          statusToCheck === "disposed" ? "disposed" :
            statusToCheck === "decided" ? "decided" :
              statusToCheck;

        // Query for existing case
        const existingCaseQuery = query(
          collection(db, collectionName),
          where("caseNo", "==", caseNo),
          where("owner", "==", user.uid)
        );

        const existingSnapshot = await getDocs(existingCaseQuery);

        // Also check other possible collections if status is unknown
        if (statusToCheck === "unknown") {
          const pendingQuery = query(
            collection(db, "pending"),
            where("caseNo", "==", caseNo),
            where("owner", "==", user.uid)
          );
          const disposedQuery = query(
            collection(db, "disposed"),
            where("caseNo", "==", caseNo),
            where("owner", "==", user.uid)
          );
          const decidedQuery = query(
            collection(db, "decided"),
            where("caseNo", "==", caseNo),
            where("owner", "==", user.uid)
          );

          const [pendingSnapshot, disposedSnapshot, decidedSnapshot] = await Promise.all([
            getDocs(pendingQuery),
            getDocs(disposedQuery),
            getDocs(decidedQuery)
          ]);

          if (!pendingSnapshot.empty || !disposedSnapshot.empty || !decidedSnapshot.empty) {
            const existingCollection = !pendingSnapshot.empty ? "pending" :
              !disposedSnapshot.empty ? "disposed" : "decided";
            const existingDoc = !pendingSnapshot.empty ? pendingSnapshot.docs[0] :
              !disposedSnapshot.empty ? disposedSnapshot.docs[0] :
                decidedSnapshot.docs[0];

            setDuplicateCaseInfo({
              caseNo: caseNo,
              collection: existingCollection,
              docId: existingDoc.id,
              data: existingDoc.data()
            });
            setDuplicateModalOpen(true);
            setExporting(false);
            return;
          }
        } else if (!existingSnapshot.empty) {
          // If case already exists in the target collection, show duplicate modal
          setDuplicateCaseInfo({
            caseNo: caseNo,
            collection: collectionName,
            docId: existingSnapshot.docs[0].id,
            data: existingSnapshot.docs[0].data()
          });
          setDuplicateModalOpen(true);
          setExporting(false);
          return;
        }
      }

      const courtName = stateCode ? getFullCourtName(stateCode) : "Unknown Court";
      const caseStatus = status?.toLowerCase() || "unknown";
      const caseFiledDate = convertHyphenToSlash(caseDetails?.caseDetails?.["Filing Date"] || "");
      const nextHearingDate = convertDateFormat(caseDetails?.caseStatus?.["Next Hearing Date"] || "");
      const hearingHistory = caseDetails?.hearingHistory;
      const hearingHistoryLength = Array.isArray(hearingHistory) ? hearingHistory.length : 0;
      let previousHearingDate = "";
      if (hearingHistoryLength > 0 && hearingHistory[1]) {
        previousHearingDate = convertHyphenToSlash(hearingHistory[1].hearingDate || "");
      }

      const petitionerAdvocate = Array.isArray(caseDetails?.petitionerAdvocate) ? caseDetails.petitionerAdvocate[0] : "";
      const respondentAdvocate = Array.isArray(caseDetails?.respondentAdvocate) ? caseDetails.respondentAdvocate[0] : "";
      const partyName = extractPartyName(petitionerAdvocate);
      const adverseParty = extractPartyName(respondentAdvocate);

      // Extract advocate names from full arrays
      const allPetitionerAdvocates = extractPetitionerAdvocates(caseDetails?.petitionerAdvocate);
      const allRespondentAdvocates = extractRespondentAdvocates(caseDetails?.respondentAdvocate);

      // Extract every petitioner/respondent name (including "...AND X OTHERS")
      // so additional parties survive export instead of only the first one.
      const allPetitioners = extractAllPartyNames(caseDetails?.petitionerAdvocate);
      const allRespondents = extractAllPartyNames(caseDetails?.respondentAdvocate);

      // Get current pending case count for subscription tracking
      let currentPendingCount = 0;
      let subscriptionMetadata = null;

      // Only count and track if we're adding to pending collection
      if (caseStatus === "pending") {
        const pendingQuery = query(
          collection(db, "pending"),
          where("owner", "==", user.uid)
        );
        const pendingSnapshot = await getDocs(pendingQuery);
        currentPendingCount = pendingSnapshot.size;

        // Get subscription metadata if case is beyond free tier
        subscriptionMetadata = await featureGatingService.getSubscriptionMetadataForNewCase(
          user.uid,
          currentPendingCount
        );

        console.log('📦 [High Court] Subscription metadata for new case:', {
          currentPendingCount,
          hasMetadata: !!subscriptionMetadata
        });
      }

      // Persist each order's PDF to Firebase Storage now, while the eCourts
      // session is still fresh. The stored URL never expires, so viewing the
      // exported case later no longer depends on the (short-lived) session
      // baked into orderLink/pdfProxyUrl.
      const ordersWithPdfs = await persistOrderPdfs(
        caseDetails?.orders || [],
        cookies,
        user.uid,
        caseDetails?.caseDetails?.["Registration Number"] || "case"
      );

      const caseData = {
        caseNo: caseDetails?.caseDetails?.["Registration Number"] || "",
        // Store cino so Court Orders can be matched back to this case later.
        cino: cino || caseDetails?.caseDetails?.["CNR Number"] || "",
        filingNumber: caseDetails?.caseDetails?.["Filing Number"] || "",
        caseType: caseDetails?.caseStatus?.["Judicial Branch"] || "",
        caseFiledDate: caseFiledDate,
        nextHearingDate: nextHearingDate,
        previousHearingDate: previousHearingDate,
        courtName: courtName,
        petitionerName: partyName,
        allPetitioners: allPetitioners, // All petitioners (main + others)
        partyContactNo: "",
        respondentName: adverseParty,
        allRespondents: allRespondents, // All respondents (main + others)
        appearingFor: "",
        petitionerAdvocate: allPetitionerAdvocates.length > 0 ? allPetitionerAdvocates[0] : "", // First petitioner advocate
        respondentAdvocate: allRespondentAdvocates.length > 0 ? allRespondentAdvocates[0] : "", // First respondent advocate
        allPetitionerAdvocates: allPetitionerAdvocates, // All petitioner advocates
        allRespondentAdvocates: allRespondentAdvocates, // All respondent advocates
        team: [userId],
        files: [],
        casenotes: "",
        status: caseStatus,
        owner: user.uid,
        caseHistory: caseDetails?.hearingHistory || [], // Add hearingHistory
        orders: ordersWithPdfs, // Orders, each enriched with a persisted pdfStorageUrl when available
        categoryDetails: caseDetails?.categoryDetails || {}, // Add category details
        payload: combinedPayload ? {
          ...combinedPayload,
          caseexportedfrom: "HighCourt",
        } : {
          courtCode: courtCode || "",
          stateCode: stateCode || "",
          courtComplexCode: courtComplexCode || "",
          cookies: cookies || {},
          searchType: "single",
          caseStatus: status || "",
          filingNumber: caseDetails?.caseDetails?.["Filing Number"] || "",
          exportedAt: new Date().toISOString(),
          caseexportedfrom: "HighCourt",
        },
        // Add subscription metadata if present (only for cases beyond free tier)
        ...(subscriptionMetadata && { subscriptionMetadata }),
        timestamp: serverTimestamp(),
      };

      const caseRef = await addDoc(collection(db, caseStatus), caseData);
      console.log("Case added to Firestore:", caseRef.id);

      // Mirror the order PDFs into the case's Files tab under an "Orders"
      // folder (named `<OrderDate>_order.pdf`).
      await saveOrderPdfsToFiles(ordersWithPdfs, caseRef.id, caseData.caseNo, caseStatus, user.uid);

      let fcmToken = "";
      try {
        fcmToken = await getToken(messaging, {
          vapidKey: "BOSi0E-WalUbqOuB5su9XW2J39ot0D4x-fSPRYo_AxMVloqe7jIkpYXf5J007lXs1D_ZZB9YH1Hc0X-7jQrqR38",
        });
      } catch (err) {
        console.warn("Could not get FCM token:", err.message);
      }

      if (nextHearingDate) {
        try {
          const eventDateStr = nextHearingDate.split("/").reverse().join("-");
          const eventDate = new Date(eventDateStr);
          if (isNaN(eventDate.getTime())) {
            console.warn("Invalid hearing date format:", nextHearingDate);
          } else {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const hearingDay = new Date(eventDate);
            hearingDay.setHours(0, 0, 0, 0);

            if (hearingDay < today) {
              console.log("Skipping event creation — next hearing date is in the past:", nextHearingDate);
            } else {
              const caseNoForEvent = caseDetails?.caseDetails?.["Registration Number"] || "";

              // Event 1: 8:00 AM IST on the next hearing date (visible)
              const reminderDate = new Date(eventDate);
              reminderDate.setHours(8, 0, 0, 0);

              // Event 2: 6:00 PM IST on the next hearing date (hidden)
              const eventTimeIST = new Date(eventDate);
              eventTimeIST.setHours(18, 0, 0, 0);

              const now = new Date();

              if (reminderDate > now) {
                const reminderEventData = {
                  "Case No": caseNoForEvent,
                  date: reminderDate.toISOString().split("T")[0],
                  id: Math.floor(Math.random() * 90000) + 10000,
                  mobile: userMobile || null,
                  time: "08:00 AM",
                  time1: reminderDate.getTime(),
                  title: `${partyName} VS ${adverseParty}`,
                  description: `Hearing Reminder : Case No. - ${caseNoForEvent}`,
                  isScheduled: reminderDate > now,
                  status: caseStatus,
                  eventType: "reminder",
                };
                const reminderEventRef = await addDoc(collection(db, "lawyers", user.uid, "events"), reminderEventData);

                const reminderDoc = {
                  eventId: reminderEventRef.id,
                  reminderTime: reminderDate,
                  eventTitle: `Hearing Reminder : Case No. - ${caseNoForEvent}`,
                  caseNo: caseNoForEvent,
                  caseId: caseRef.id,
                  collection: caseStatus,
                  status: "pending",
                  createdAt: new Date(),
                  scheduledBy: user.uid,
                  recipientId: user.uid,
                };
                await addDoc(collection(db, "eventReminders"), reminderDoc);
              }

              if (eventTimeIST > now) {
                const mainEventData = {
                  "Case No": caseNoForEvent,
                  date: eventDate.toISOString().split("T")[0],
                  id: Math.floor(Math.random() * 90000) + 10000,
                  mobile: userMobile || null,
                  time: "06:00 PM",
                  time1: eventTimeIST.getTime(),
                  title: `Update Next Hearing: ${partyName} VS ${adverseParty}`,
                  description: `Case No. - ${caseNoForEvent}`,
                  isScheduled: eventTimeIST > now,
                  status: caseStatus,
                  eventType: "updateReminder",
                  isHidden: true,
                };
                const mainEventRef = await addDoc(collection(db, "lawyers", user.uid, "events"), mainEventData);

                const mainReminderDoc = {
                  eventId: mainEventRef.id,
                  reminderTime: eventTimeIST,
                  eventTitle: `Update Next Hearing : ${caseNoForEvent}`,
                  caseNo: caseNoForEvent,
                  caseId: caseRef.id,
                  collection: caseStatus,
                  status: "pending",
                  createdAt: new Date(),
                  scheduledBy: user.uid,
                  recipientId: user.uid,
                };
                await addDoc(collection(db, "eventReminders"), mainReminderDoc);
              }
            }
          }
        } catch (notificationError) {
          console.error("Notification setup error:", notificationError);
        }
      }

      showInfo();
    } catch (error) {
      console.error("Export error:", error);
      alert(`Error exporting case: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

const handleViewSpecificOrder = async (orderLink, pdfProxyUrl) => {
  if (!orderLink && !pdfProxyUrl) {
    alert("No order link available");
    return;
  }
  try {
    if (pdfProxyUrl) {
      const res = await fetch(`${HC_API_HOST}${pdfProxyUrl}`);
      if (!res.ok) throw new Error("Failed to fetch order PDF");
      const blob = await res.blob();
      const pdfUrl = URL.createObjectURL(blob);
      window.open(pdfUrl, "_blank");
    } else {
      const hcservices_sessid = cookies?.HCSERVICES_SESSID || "";
      const jsession_value = cookies?.JSESSION || cookies?.JSESSIONID || "";
      const pdfBlob = await apiRequest("case/orders/pdf", {
        orderLink,
        hcservices_sessid,
        jsession_value,
      });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, "_blank");
    }
  } catch (err) {
    console.error("Error fetching order PDF:", err);
    alert("Failed to load order PDF. Please try again.");
  }
};


  if (!caseDetails) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] p-4 md:p-8">
        <div className="text-slate-500 text-center mt-10">No case details available</div>
      </div>
    );
  }


  const petitionerName = extractPartyName(Array.isArray(caseDetails?.petitionerAdvocate) ? caseDetails.petitionerAdvocate[0] : "");
  const respondentName = extractPartyName(Array.isArray(caseDetails?.respondentAdvocate) ? caseDetails.respondentAdvocate[0] : "");

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-800">
      <div className="px-4 sm:px-6 lg:px-8 py-4">
        {/* Breadcrumb */}
        <div className="mb-4 flex items-center gap-1.5 text-sm">
          <button onClick={() => navigate("/portal/findCasesOptions")} className="text-slate-500 hover:text-slate-800">Find Cases</button>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <button onClick={() => navigate(-1)} className="text-slate-500 hover:text-slate-800">Results</button>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-800">Import Case</span>
        </div>

        {/* Stepper */}
        <div className="mb-4"><FindCaseStepper current={4} /></div>

        <div className="mb-4">
          <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">Review Case Information</h1>
          <p className="mt-0.5 text-xs text-slate-500">Review the details before importing this case.</p>
        </div>

        <div className="space-y-4">
          {/* Header Card */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-5">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold">{petitionerName}</h2>
              <span className={`px-3 py-1 rounded-full text-sm text-white ${statusPillSolid(caseDetails?.caseStatus?.["Stage of Case"])}`}>
                {caseDetails?.caseStatus?.["Stage of Case"] || "Unknown Status"}
              </span>
            </div>
          </div>

          {/* Case Information */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-5">
            <h3 className="text-lg font-bold mb-2">Case Information</h3>
            <hr className="border-slate-200 mb-4" />
            {caseDetails?.caseDetails && Object.entries(caseDetails.caseDetails).map(([key, value]) => (
              <InfoRow key={key} label={key} value={value} />
            ))}
          </div>

          {/* Petitioner and Advocate */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-5">
            <h3 className="text-lg font-bold mb-2">Petitioner and Advocate</h3>
            <hr className="border-slate-200 mb-4" />
            {Array.isArray(caseDetails?.petitionerAdvocate) && caseDetails.petitionerAdvocate.map((item, index) => (
              <p key={index} className="py-1 text-sm">{item}</p>
            ))}
          </div>

          {/* Respondent and Advocate */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-5">
            <h3 className="text-lg font-bold mb-2">Respondent and Advocate</h3>
            <hr className="border-slate-200 mb-4" />
            {Array.isArray(caseDetails?.respondentAdvocate) && caseDetails.respondentAdvocate.map((item, index) => (
              <p key={index} className="py-1 text-sm">{item}</p>
            ))}
          </div>

          {/* Case Status */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-5">
            <h3 className="text-lg font-bold mb-2">Case Status</h3>
            <hr className="border-slate-200 mb-4" />
            {caseDetails?.caseStatus && Object.entries(caseDetails.caseStatus).map(([key, value]) => (
              <InfoRow key={key} label={key} value={value} />
            ))}
          </div>

          {/* Category Details */}
          {caseDetails?.categoryDetails && (
            <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-5">
              <h3 className="text-lg font-bold mb-2">Category Details</h3>
              <hr className="border-slate-200 mb-4" />
              {Object.entries(caseDetails.categoryDetails).map(([key, value]) => (
                <InfoRow key={key} label={key} value={value} />
              ))}
            </div>
          )}

          {/* Hearing History */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-5">
            <h3 className="text-lg font-bold mb-2">Hearing History</h3>
            <hr className="border-slate-200 mb-4" />
            <table className="w-full text-left">
              <thead>
                <tr className="text-slate-600">
                  <th className="py-2">Hearing Date</th>
                  <th className="py-2">Judge</th>
                  <th className="py-2">Purpose</th>
                  <th className="py-2">Cause List Type</th>
                  <th className="py-2">Business On Date</th>
                </tr>
              </thead>
              <tbody>
                {caseDetails?.hearingHistory?.map((hearing, index) => (
                  <tr key={index} className="border-t border-slate-100">
                    <td className="py-2">{hearing.hearingDate || "N/A"}</td>
                    <td className="py-2">{hearing.judge || "N/A"}</td>
                    <td className="py-2">{hearing.purpose || "N/A"}</td>
                    <td className="py-2">{hearing.causeListType || "N/A"}</td>
                    <td className="py-2">{hearing.businessOnDate || "N/A"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Orders */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-5">
            <h3 className="text-lg font-bold mb-2">Orders</h3>
            <hr className="border-slate-200 mb-4" />
            <table className="w-full text-left">
              <thead>
                <tr className="text-slate-600">
                  <th className="py-2">Order Number</th>
                  <th className="py-2">Order On</th>
                  <th className="py-2">Order Date</th>
                  <th className="py-2">Judge</th>
                  <th className="py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {caseDetails?.orders?.map((order, index) => (
                  <tr key={index} className="border-t border-slate-100">
                    <td className="py-2">{order.orderNumber || "N/A"}</td>
                    <td className="py-2">{order.orderOn || "N/A"}</td>
                    <td className="py-2">{order.orderDate || "N/A"}</td>
                    <td className="py-2">{order.judge || "N/A"}</td>
                    <td className="py-2">
                      <button
                        onClick={() =>
                          order.pdfStorageUrl
                            ? window.open(order.pdfStorageUrl, "_blank", "noopener,noreferrer")
                            : handleViewSpecificOrder(order.orderLink, order.pdfProxyUrl)
                        }
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Buttons */}
          <div className="flex justify-end">
            <button
              onClick={handleExportCase}
              disabled={exporting}
              className={`bg-yellow-500 text-gray-900 py-2.5 px-8 rounded-lg flex items-center justify-center font-semibold text-sm shadow-sm hover:bg-yellow-600 transition-colors ${exporting ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {exporting ? "Importing..." : "Import Case"}
            </button>
          </div>
        </div>

      <InfoModal
        isOpen={isInfoModalOpen}
        onClose={closeInfoModal}
        title="Information"
        message="Case exported successfully! For exported case the default reminder is set to 1 day before the next hearing date."
      />
      <InfoModal
        isOpen={duplicateModalOpen}
        onClose={() => setDuplicateModalOpen(false)}
        title="Duplicate Case Found"
        message={`Case ${duplicateCaseInfo?.caseNo} is already exported and saved in your profile under ${duplicateCaseInfo?.collection} cases.`}
      />

      {/* Case Limit Dialog */}
      <CaseLimitDialog
        isOpen={showCaseLimitDialog}
        onClose={() => setShowCaseLimitDialog(false)}
        currentCount={caseLimitData.currentCount}
        maxLimit={caseLimitData.maxLimit}
        planName={caseLimitData.planName}
      />
      </div>
    </div>
  );
}

const InfoRow = ({ label, value }) => (
  <div className="flex py-1">
    <span className="w-36 text-sm text-slate-600">{label}:</span>
    <span className="font-bold flex-1">{value || "N/A"}</span>
  </div>
);

export default CaseDetailsScreen;